/**
 * Hooks for the OCR module. Matches your codebase's apiClient pattern
 * and your conventions of returning `data` directly (not wrapped).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface OcrResult {
  id: string;
  trip_id: string;
  trip_photo_id: string;
  template_code: string;
  engine: string;
  engine_version: string | null;

  raw_text: string | null;
  raw_blocks: unknown;

  m3_value: string | null;
  m3_raw: string | null;
  m3_confidence: string | null;

  voucher_number: string | null;
  voucher_raw: string | null;
  voucher_confidence: string | null;

  ocr_time: string | null;
  time_raw: string | null;
  time_confidence: string | null;

  ocr_date: string | null;
  date_raw: string | null;
  date_confidence: string | null;
  date_source: string | null;

  vehicle_plate: string | null;
  vehicle_raw: string | null;
  vehicle_confidence: string | null;

  driver_name: string | null;
  driver_raw: string | null;
  driver_confidence: string | null;

  site_code: string | null;
  site_raw: string | null;
  site_confidence: string | null;

  overall_confidence: string | null;
  status: string;
  failure_reason: string | null;
  failure_detail: string | null;

  reviewed_by: string | null;
  reviewed_at: string | null;
  review_action: string | null;
  review_notes: string | null;

  ocr_duration_ms: number | null;
  retry_count: number;

  created_at: string;
  updated_at: string;

  // Joined fields (from listForReview)
  trip_code?: string;
  trip_date?: string;
  trip_vehicle_plate?: string;
  trip_driver_name?: string;
  trip_site_name?: string;
  photo_path?: string;
}

export interface OcrSettings {
  id: string;
  confidence_threshold: string;
  m3_min: string;
  m3_max: string;
  date_tolerance_days: number;
  auto_apply_enabled: boolean;
  engine_primary: string;
  engine_fallback: string | null;
  max_retries: number;
}

export interface OcrStats {
  total: number;
  extracted: number;
  review_needed: number;
  failed: number;
  confirmed: number;
  avg_duration_ms: number;
  avg_confidence: number;
  queue_depth: number;
  pending_review: number;
  window_days: number;
}

interface OcrListResponse {
  data: OcrResult[];
  total: number;
  pending_review: number;
}

// ----------------------------------------------------------------- LIST

export function useOcrReviewList(filters: {
  status?: string[];
  siteId?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
} = {}) {
  return useQuery({
    queryKey: ['ocr-review-list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      filters.status?.forEach((s) => params.append('status', s));
      if (filters.siteId) params.set('site_id', filters.siteId);
      if (filters.fromDate) params.set('from_date', filters.fromDate);
      if (filters.toDate) params.set('to_date', filters.toDate);
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));
      const { data } = await apiClient.get<OcrListResponse>(`/api/v1/ocr-results?${params}`);
      return data;
    },
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

// ----------------------------------------------------------------- DETAIL

export function useOcrResult(id: string | null | undefined) {
  return useQuery({
    queryKey: ['ocr-result', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: OcrResult }>(`/api/v1/ocr-results/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useTripOcrResult(tripId: string | null | undefined) {
  return useQuery({
    queryKey: ['trip-ocr-result', tripId],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<{ data: OcrResult }>(`/api/v1/trips/${tripId}/ocr-result`);
        return data.data;
      } catch (err: unknown) {
        // 404 is expected when no OCR has run yet
        const e = err as { response?: { status?: number } };
        if (e?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!tripId,
    refetchInterval: (query) => {
      const status = (query.state.data as OcrResult | null | undefined)?.status;
      if (status === 'pending' || status === 'processing') return 5_000;
      return false;
    },
  });
}

// ----------------------------------------------------------------- ACTIONS

export function useAcceptOcr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: OcrResult }>(`/api/v1/ocr-results/${id}/accept`, {});
      return data.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['ocr-review-list'] });
      qc.invalidateQueries({ queryKey: ['ocr-result', data.id] });
      qc.invalidateQueries({ queryKey: ['trip-ocr-result', data.trip_id] });
      qc.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}

export function useCorrectOcr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        m3_value?: number;
        voucher_number?: string;
        ocr_time?: string;
        ocr_date?: string;
      };
    }) => {
      const { data } = await apiClient.patch<{ data: OcrResult }>(`/api/v1/ocr-results/${id}`, patch);
      return data.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['ocr-review-list'] });
      qc.invalidateQueries({ queryKey: ['ocr-result', data.id] });
      qc.invalidateQueries({ queryKey: ['trip-ocr-result', data.trip_id] });
      qc.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}

export function useRejectOcr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const { data } = await apiClient.post<{ data: OcrResult }>(`/api/v1/ocr-results/${id}/reject`, { notes });
      return data.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['ocr-review-list'] });
      qc.invalidateQueries({ queryKey: ['ocr-result', data.id] });
      qc.invalidateQueries({ queryKey: ['trip-ocr-result', data.trip_id] });
    },
  });
}

export function useRetriggerOcr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      const { data } = await apiClient.post(`/api/v1/trips/${tripId}/ocr/retrigger`, {});
      return data;
    },
    onSuccess: (_d, tripId) => {
      qc.invalidateQueries({ queryKey: ['trip-ocr-result', tripId] });
      qc.invalidateQueries({ queryKey: ['trips'] });
    },
  });
}

// ----------------------------------------------------------------- STATS / SETTINGS

export function useOcrStats(days = 30) {
  return useQuery({
    queryKey: ['ocr-stats', days],
    queryFn: async () => {
      const { data } = await apiClient.get<OcrStats>(`/api/v1/ocr/stats?days=${days}`);
      return data;
    },
    refetchInterval: 60_000,
  });
}

export function useOcrSettings() {
  return useQuery({
    queryKey: ['ocr-settings'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: OcrSettings }>(`/api/v1/ocr/settings`);
      return data.data;
    },
  });
}

export function useUpdateOcrSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<OcrSettings>) => {
      const { data } = await apiClient.patch<{ data: OcrSettings }>(`/api/v1/ocr/settings`, patch);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ocr-settings'] });
    },
  });
}

// ----------------------------------------------------------------- HELPERS

export function confidenceBand(c: string | number | null | undefined): {
  label: string;
  color: 'green' | 'amber' | 'red' | 'gray';
} {
  if (c === null || c === undefined) return { label: '—', color: 'gray' };
  const n = typeof c === 'string' ? parseFloat(c) : c;
  if (isNaN(n)) return { label: '—', color: 'gray' };
  if (n >= 0.85) return { label: `${(n * 100).toFixed(0)}%`, color: 'green' };
  if (n >= 0.6) return { label: `${(n * 100).toFixed(0)}%`, color: 'amber' };
  return { label: `${(n * 100).toFixed(0)}%`, color: 'red' };
}
